import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { calcMonthly, formatEuro } from "../lib/finance";
import { ArrowLeft, Calendar, MessageCircle, ChevronLeft, ChevronRight } from "lucide-react";

export default function BikeDetail() {
    const { id } = useParams();
    const { lang, t } = useLang();
    const [bike, setBike] = useState(null);
    const [imgIdx, setImgIdx] = useState(0);

    useEffect(() => {
        api.get(`/bikes/${id}`).then((r) => {
            setBike(r.data);
            setImgIdx(0);
        });
    }, [id]);

    if (!bike) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center text-neutral-500 font-mono text-sm">
                {t.misc.loading}
            </div>
        );
    }

    const description = lang === "es" ? bike.description_es : bike.description_en;
    const images = bike.images?.length ? bike.images : ["https://images.pexels.com/photos/23524306/pexels-photo-23524306.jpeg"];

    return (
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-20" data-testid="bike-detail-page">
            <Link
                to={bike.condition === "used" ? "/ocasion" : "/motos"}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-900"
                data-testid="back-link"
            >
                <ArrowLeft size={14} />
                {bike.condition === "used" ? t.nav.used : t.nav.new}
            </Link>

            <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-12">
                {/* Gallery */}
                <div className="lg:col-span-7">
                    <div className="relative aspect-[4/3] overflow-hidden border border-black/10 bg-neutral-100">
                        <img src={images[imgIdx]} alt={bike.name} className="h-full w-full object-cover" />
                        {images.length > 1 && (
                            <>
                                <button
                                    onClick={() => setImgIdx((p) => (p - 1 + images.length) % images.length)}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/85 backdrop-blur-md border border-black/15 p-3 hover:bg-white/90"
                                    data-testid="gallery-prev"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <button
                                    onClick={() => setImgIdx((p) => (p + 1) % images.length)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/85 backdrop-blur-md border border-black/15 p-3 hover:bg-white/90"
                                    data-testid="gallery-next"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </>
                        )}
                        <span
                            className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5"
                            style={{
                                background: bike.condition === "used" ? "var(--ls-terracotta)" : "var(--ls-blue)",
                                color: "white",
                            }}
                        >
                            {bike.condition === "used" ? t.bike.condition_used : t.bike.condition_new}
                        </span>
                    </div>
                    {images.length > 1 && (
                        <div className="mt-3 grid grid-cols-5 gap-2">
                            {images.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => setImgIdx(i)}
                                    data-testid={`thumb-${i}`}
                                    className={`aspect-[4/3] overflow-hidden border transition-all ${
                                        i === imgIdx ? "border-black" : "border-black/10 opacity-60 hover:opacity-100"
                                    }`}
                                >
                                    <img src={img} alt="" className="h-full w-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}

                    {bike.video_url && (
                        <div className="mt-8">
                            <p className="text-overline mb-3">{t.bike.video}</p>
                            <div className="aspect-video w-full overflow-hidden border border-black/10 bg-neutral-100">
                                <iframe
                                    src={bike.video_url}
                                    title={bike.name}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="h-full w-full"
                                    data-testid="bike-video"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="lg:col-span-5">
                    <p className="text-overline">{bike.category} · {bike.year}</p>
                    <h1 className="mt-3 font-display text-5xl font-bold tracking-tighter sm:text-6xl">{bike.name}</h1>

                    <div className="mt-8 border-t border-b border-black/10 py-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                            {t.bike.from_month}
                        </p>
                        <p className="mt-1 font-display text-6xl font-bold leading-none" style={{ color: "var(--ls-terracotta)" }}>
                            {calcMonthly(bike.price)}<span className="text-3xl">{t.bike.per_month}</span>
                        </p>
                        <p className="mt-4 font-mono text-sm text-neutral-600">
                            {t.bike.from} <span className="text-neutral-900 font-semibold">{formatEuro(bike.price)}</span>
                        </p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            {t.bike.finance_note}
                        </p>
                        {bike.condition === "used" && bike.mileage > 0 && (
                            <p className="mt-4 font-mono text-sm text-neutral-500">
                                {bike.mileage.toLocaleString("es-ES")} km · {bike.color}
                            </p>
                        )}
                    </div>

                    <p className="mt-6 text-base text-neutral-600 leading-relaxed">{description}</p>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link
                            to={`/contacto?type=test-ride&bike=${bike.id}`}
                            className="btn-primary"
                            data-testid="bike-test-ride-btn"
                        >
                            <Calendar size={16} />
                            {t.bike.test_ride}
                        </Link>
                        <Link
                            to={`/contacto?bike=${bike.id}`}
                            className="btn-outline"
                            data-testid="bike-ask-btn"
                        >
                            <MessageCircle size={16} />
                            {t.bike.ask}
                        </Link>
                    </div>

                    {/* Licence + type badges */}
                    <div className="mt-6 flex flex-wrap gap-2">
                        {bike.licence_class && (
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-black/15 px-3 py-1.5 text-neutral-600" data-testid="bike-licence">
                                {t.bike.licence}: {bike.licence_class}
                            </span>
                        )}
                        {bike.bike_type && (
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-black/15 px-3 py-1.5 text-neutral-600" data-testid="bike-type">
                                {bike.bike_type === "scooter" ? t.misc.scooter : t.misc.bike_moto}
                            </span>
                        )}
                    </div>

                    {/* Trust block for used bikes */}
                    {bike.condition === "used" && (
                        <div className="mt-10 border border-black/10 p-6" data-testid="trust-block">
                            <p className="text-overline mb-4">{t.bike.trust_title}</p>
                            <ul className="space-y-2 text-sm text-neutral-600">
                                {bike.service_history && <li>· {t.bike.trust_service}</li>}
                                {bike.keys_count && <li>· {bike.keys_count} {t.bike.trust_keys}</li>}
                                {bike.tyre_state && <li>· {t.bike.trust_tyres}: {bike.tyre_state}</li>}
                                {bike.next_itv && <li>· {t.bike.trust_itv}: {bike.next_itv}</li>}
                                {bike.warranty && <li>· {t.bike.trust_warranty}: {bike.warranty}</li>}
                                {bike.financing_available && <li>· {t.bike.trust_financing}</li>}
                                {bike.home_delivery && <li>· {t.bike.trust_delivery}</li>}
                            </ul>
                        </div>
                    )}

                    {/* Financing block */}
                    <div className="mt-8 border border-black/10 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="financing-block">
                        <div>
                            <p className="text-overline" style={{ color: "var(--ls-terracotta)" }}>
                                {t.bike.financing}
                            </p>
                            <p className="mt-2 text-sm text-neutral-600">{t.bike.financing_desc}</p>
                        </div>
                        <Link
                            to={`/contacto?bike=${bike.id}`}
                            className="btn-accent shrink-0"
                            data-testid="financing-cta"
                        >
                            {t.bike.financing_cta}
                        </Link>
                    </div>

                    {/* Specs */}
                    <div className="mt-12">
                        <p className="text-overline mb-4">{t.bike.specs}</p>
                        <div className="border border-black/10">
                            {Object.entries(bike.specs || {}).map(([k, v]) => (
                                <div
                                    key={k}
                                    className="grid grid-cols-2 border-b border-black/10 last:border-0 px-5 py-4"
                                >
                                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-neutral-500">
                                        {k.replace(/_/g, " ")}
                                    </p>
                                    <p className="font-mono text-sm text-neutral-900 text-right">{v}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
